import { useCallback, useMemo } from "react";
import { useTTS } from "@/providers/TTSProvider";

export interface UseTextToSpeechOptions {
  sectionId?: string;
  slideId?: string;
  /** Client-only cache/playback key, never sent over the wire. Overrides
   * slideId/sectionId for local dedup when neither is a real backend id. */
  cacheKey?: string;
  /** Resolved voice for this section/slide, e.g. from useSectionVoiceId.
   * Overrides the TTSProvider's default voiceId for cache comparisons, so
   * a per-section voice change busts stale cached audio. */
  voiceId?: string;
}

/**
 * Hook for text-to-speech playback. Must be used within TTSProvider.
 *
 * This hook provides TTS functionality scoped to a specific section/slide.
 * The `isPlaying`, `isPaused`, and `isLoading` states reflect whether THIS
 * specific section/slide is playing, not global playback state.
 *
 * For raw access to global TTS state, use useTTS() instead.
 *
 * @param options - Object containing sectionId and/or slideId to scope playback state
 * @returns TTS control functions and state
 * @throws Error if used outside of TTSProvider
 *
 * @example
 * // For sections:
 * const { play, pause, isPlaying } = useTextToSpeech({ sectionId: section.id });
 * play(scriptText);
 *
 * // For slides:
 * const { play, pause, isPlaying } = useTextToSpeech({ slideId: slide.uuid });
 * play(scriptText);
 */
export const useTextToSpeech = (options: UseTextToSpeechOptions = {}) => {
  const { sectionId, slideId, cacheKey, voiceId } = options;

  const {
    play: globalPlay,
    pause,
    resume: globalResume,
    stop,
    currentlyPlayingId,
    isPlaying: globalIsPlaying,
    isPaused: globalIsPaused,
    isLoading: globalIsLoading,
    isConnected,
    hasError,
    wsBusy,
  } = useTTS();

  const playbackId = cacheKey ?? slideId ?? sectionId;
  const isThisPlaying = currentlyPlayingId === playbackId;

  const isPlaying = isThisPlaying && globalIsPlaying;
  const isPaused = isThisPlaying && globalIsPaused;
  const isLoading = isThisPlaying && globalIsLoading;

  const play = useCallback(
    (text?: string) => {
      globalPlay({ sectionId, slideId, text, cacheKey, voiceId });
    },
    [globalPlay, sectionId, slideId, cacheKey, voiceId]
  );

  const resume = useCallback(
    (text?: string) =>
      globalResume({ sectionId, slideId, text, cacheKey, voiceId }),
    [globalResume, sectionId, slideId, cacheKey, voiceId]
  );

  return useMemo(
    () => ({
      isPlaying,
      isPaused,
      isLoading,
      isConnected,
      hasError,
      wsBusy,
      play,
      pause,
      resume,
      stop,
    }),
    [
      isPlaying,
      isPaused,
      isLoading,
      isConnected,
      hasError,
      wsBusy,
      play,
      pause,
      resume,
      stop,
    ]
  );
};
